import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, Checkbox, Layout, Text} from '@phonectas/unifon-ui-kit';
import {unifonEmailValid} from 'utils/email';
import EmailTable from './EmailTable';
import {StyledTextFieldXl} from './styled';
import {BmoForm} from 'common/types/store/bmoTypes';
import UserModel from 'common/models/user';

interface EmailNoticeProps {
	form: BmoForm;
	user: UserModel | null;
	onChange: (name: string, value: string | string[]) => void;
}

const EmailNotice: React.FC<EmailNoticeProps> = ({form, user, onChange}) => {
	const [t] = useTranslation();
	const userEmailAdded = useRef(false);
	const [activateNotice, setActivateNotice] = useState(true);
	const [addEmail, setAddEmail] = useState('');
	const [ErrorMsg, setErrorMsg] = useState('');
	const handleActiveNotice = useCallback(() => {
		setActivateNotice((prevActivateNotice) => !prevActivateNotice);
	}, []);
	const disableAddEmail = (form.notificationEmails ?? []).length >= 10;

	useEffect(() => {
		if (!activateNotice) {
			onChange('notificationEmails', []);
			setAddEmail('');
			setErrorMsg('');
			userEmailAdded.current = false;
		} else if (user?.email && activateNotice && !userEmailAdded.current) {
			const userEmail = user.email;
			if (!(form.notificationEmails ?? []).includes(userEmail)) {
				onChange('notificationEmails', [...(form.notificationEmails ?? []), userEmail]);
			}
			userEmailAdded.current = true;
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activateNotice, user, onChange]);

	// Clear form/submit resets the list to the user's email; keep it empty while notifications are off.
	useEffect(() => {
		if (!activateNotice && (form.notificationEmails ?? []).length > 0) {
			onChange('notificationEmails', []);
		}
	}, [activateNotice, form.notificationEmails, onChange]);

	const onValidate = useCallback(() => {
		let isValid;
		if (unifonEmailValid(addEmail)) {
			const cleanEmail = addEmail.trim().toLowerCase();
			if (!((form.notificationEmails ?? []).map((email) => email.trim().toLowerCase()).includes(cleanEmail)) &&
			(form.notificationEmails ?? []).length < 10) {
				isValid = true;
			}
		} else {
			isValid = false;
		}
		return isValid;
	}, [addEmail, form.notificationEmails]);

	const handleAddEmail = useCallback((val: React.ChangeEvent<HTMLInputElement>) => {
		setAddEmail(val.target.value);
	}, [setAddEmail]);

	const onAddEmail = useCallback(() => {
		if (onValidate()) {
			onChange('notificationEmails', [...(form.notificationEmails ?? []), addEmail]);
			setAddEmail('');
			setErrorMsg('');
		} else {
			setErrorMsg(t('Must be valid Email and no duplicates'));
		}
	}, [form.notificationEmails, addEmail, onChange, t, onValidate]);

	const handleDeleteEmail = useCallback((index: number) => {
		const updatedEmails = (form.notificationEmails ?? []).toSpliced(index, 1);
		onChange('notificationEmails', updatedEmails);
	}, [form.notificationEmails, onChange]);

	return (
		<Layout fullWidth direction="column">
			<Checkbox
				big
				toggle
				label={t('Email notification')}
				checked={activateNotice}
				onChange={handleActiveNotice}
			/>
			{
				activateNotice && (
					<Layout fullWidth direction="column" nowrap>
						<Text color="error">{ErrorMsg}</Text>
						<Layout
							direction="row"
							alignItems="end"
							justifyContent="space-between"
							fullWidth
							nowrap
						>
							<StyledTextFieldXl
								fullWidth
								name="email"
								label={t('Add email')}
								placeholder={t('name.lastname@unifon.no')}
								value={addEmail}
								onChange={handleAddEmail}
							/>
							<Button
								variant='secondary'
								onClick={onAddEmail}
								disabled={disableAddEmail}
							>
								{t('Add')}
							</Button>
						</Layout>
						<Layout
							direction="row"
							fullWidth
						>
							<EmailTable
								// form={form}
								form={{
									...form,
									notificationEmails: form.notificationEmails ?? [],
								}}
								onDelete={handleDeleteEmail}
							/>
						</Layout>
					</Layout>
				)
			}
		</Layout>
	);
}

export default EmailNotice;
